<template>
  <jd-tooltip
    ref="refPopper"
    :visible="pickerVisible"
    effect="light"
    pure
    trigger="click"
    v-bind="$attrs"
    role="dialog"
    teleported
    :transition="`${nsDate.namespace.value}-zoom-in-top`"
    :popper-class="[`${nsDate.namespace.value}-picker__popper`, popperClass]"
    :popper-options="elPopperOptions"
    :fallback-placements="['bottom', 'top', 'right', 'left']"
    :placement="placement"
    :gpu-acceleration="false"
    :stop-popper-mouse-event="false"
    :hide-after="0"
    :show-arrow="false"
    :offset="4"
    persistent
    @before-show="onBeforeShow"
    @show="onShow"
    @hide="onHide"
  >
    <template #default>
      <!-- 自定义触发器 -->
      <slot
        v-if="customTrigger && $slots.trigger"
        name="trigger"
        :input-ref="inputRef"
        :display-value="displayValue"
        :placeholder="
          isRangeInput ? [startPlaceholder, endPlaceholder] : placeholder
        "
        :disabled="true"
        :readonly="
          !editable ||
          readonly ||
          isDatesPicker ||
          type === 'week' ||
          isQuarterPicker
        "
        :size="pickerSize"
        :is-range="isRangeInput"
        :show-close="showClose"
        :trigger-icon="triggerIcon"
        :clear-icon="clearIcon"
        :on-mouse-down="onMouseDownInput"
        :on-focus="handleFocusInput"
        :on-blur="handleBlurInput"
        :on-input="onUserInput"
        :on-change="handleChange"
        :on-keydown="handleKeydownInput"
        :on-clear="onClearIconClick"
        :on-mouse-enter="onMouseEnter"
        :on-mouse-leave="onMouseLeave"
        :on-touch-start="onTouchStartInput"
        :handle-start-input="handleStartInput"
        :handle-end-input="handleEndInput"
        :handle-start-change="handleStartChange"
        :handle-end-change="handleEndChange"
        :class-obj="{
          editor: [nsDate.b('editor'), nsDate.bm('editor', type), $attrs.class],
          rangeInput: rangeInputKls,
          clearIcon: clearIconKls,
          rangeInputClass: nsRange.b('input'),
          rangeSeparator: nsRange.b('separator'),
          iconClass: nsInput.e('icon'),
        }"
        :style-obj="$attrs.style"
      />
      <!-- 默认输入框触发器 -->
      <template v-else>
        <jd-input
          v-if="!isRangeInput"
          :id="(id as string | undefined)"
          ref="inputRef"
          container-role="combobox"
          :model-value="(displayValue as string)"
          :name="name"
          :size="pickerSize"
          :disabled="pickerDisabled"
          :placeholder="placeholder"
          :data-component-name="trackEventName ? trackComponentName : undefined"
          :data-event-name="trackEventName"
          :data-event-content="trackEventContent"
          :class="[nsDate.b('editor'), nsDate.bm('editor', type), $attrs.class]"
          :style="$attrs.style"
          :readonly="
            !editable ||
            readonly ||
            isDatesPicker ||
            type === 'week' ||
            isQuarterPicker
          "
          :label="label"
          :tabindex="tabindex"
          :validate-event="false"
          @input="onUserInput"
          @focus="handleFocusInput"
          @blur="handleBlurInput"
          @keydown="
            //
            handleKeydownInput as any
          "
          @change="handleChange"
          @mousedown="onMouseDownInput"
          @mouseenter="onMouseEnter"
          @mouseleave="onMouseLeave"
          @touchstart="onTouchStartInput"
          @click.stop
        >
          <template v-if="$slots.prepend" #prepend>
            <slot name="prepend" />
          </template>
          <template #suffix>
            <jd-icon
              v-if="triggerIcon && !showClose"
              :class="nsInput.e('icon')"
              @mousedown.prevent="onMouseDownInput"
              @touchstart="onTouchStartInput"
            >
              <component :is="triggerIcon" />
            </jd-icon>
            <jd-icon
              v-if="showClose && clearIcon"
              :class="`${nsInput.e('icon')} clear-icon`"
              :data-component-name="trackComponentName"
              data-event-name="clear"
              @click.stop="onClearIconClick"
            >
              <component :is="clearIcon" />
            </jd-icon>
          </template>
        </jd-input>
        <div
          v-else
          ref="inputRef"
          :class="rangeInputKls"
          :style="($attrs.style as any)"
          @click="handleFocusInput"
          @mouseenter="onMouseEnter"
          @mouseleave="onMouseLeave"
          @touchstart="onTouchStartInput"
          @keydown="handleKeydownInput"
        >
          <input
            :id="id && id[0]"
            autocomplete="off"
            :name="name && name[0]"
            :placeholder="startPlaceholder"
            :value="displayValue && displayValue[0]"
            :disabled="pickerDisabled"
            :readonly="
              !editable || readonly || isQuarterPicker || isWeekRangePicker
            "
            :class="nsRange.b('input')"
            :data-component-name="trackEventName ? trackComponentName : undefined"
            :data-event-name="trackEventName"
            :data-event-content="trackEventContent"
            @mousedown="onMouseDownInput"
            @input="handleStartInput"
            @change="handleStartChange"
            @focus="handleFocusInput"
            @blur="handleBlurInput"
          />
          <slot name="range-separator">
            <span :class="nsRange.b('separator')">{{ rangeSeparator }}</span>
          </slot>
          <input
            :id="id && id[1]"
            autocomplete="off"
            :name="name && name[1]"
            :placeholder="endPlaceholder"
            :value="displayValue && displayValue[1]"
            :disabled="pickerDisabled"
            :readonly="
              !editable || readonly || isQuarterPicker || isWeekRangePicker
            "
            :class="nsRange.b('input')"
            :data-component-name="trackEventName ? trackComponentName : undefined"
            :data-event-name="trackEventName"
            :data-event-content="trackEventContent"
            @mousedown="onMouseDownInput"
            @focus="handleFocusInput"
            @blur="handleBlurInput"
            @input="handleEndInput"
            @change="handleEndChange"
          />
          <jd-icon
            v-if="clearIcon"
            key="clearIcon"
            :class="clearIconKls"
            :data-component-name="trackComponentName"
            data-event-name="clear"
            @click.stop="onClearIconClick"
            @mousedown.stop
            @touchstart.stop
            @keydown.stop
          >
            <component :is="clearIcon" />
          </jd-icon>
          <jd-icon
            v-if="triggerIcon && !showClose"
            :class="[nsInput.e('icon'), nsRange.e('icon')]"
            @mousedown="onMouseDownInput"
            @touchstart="onTouchStartInput"
          >
            <component :is="triggerIcon" />
          </jd-icon>
        </div>
      </template>
    </template>
    <template #content>
      <slot
        :visible="pickerVisible"
        :actual-visible="pickerActualVisible"
        :parsed-value="parsedValue"
        :format="format"
        :unlink-panels="unlinkPanels"
        :type="type"
        :default-value="defaultValue"
        :clear-on-close="clearOnClose"
        :range-limit="rangeLimit"
        @pick="onPick"
        @select-range="setSelectionRange"
        @set-picker-option="onSetPickerOption"
        @calendar-change="onCalendarChange"
        @panel-change="onPanelChange"
        @keydown="onKeydownPopperContent"
        @mousedown.stop
      />
    </template>
  </jd-tooltip>
</template>
<script lang="ts" setup>
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  provide,
  ref,
  unref,
  useAttrs,
  watch,
} from 'vue'
import { isEqual } from 'lodash-unified'
import { onClickOutside } from '@vueuse/core'
import { Calendar, Clock } from '@jd/jdesign-icons-vue'
import dayjs from 'dayjs'
import { useLocale, useNamespace } from '@jdesign-vue/hooks'
import { useFormItem, useFormSize } from '@jdesign-vue/components/form'
import JdInput from '@jdesign-vue/components/input'
import JdIcon from '@jdesign-vue/components/icon'
import JdTooltip from '@jdesign-vue/components/tooltip'
import { debugWarn, isArray } from '@jdesign-vue/utils'
import { EVENT_CODE } from '@jdesign-vue/constants'
import { formatter, parseDate, valueEquals } from '../utils'
import { timePickerDefaultProps } from './props'

import type { Dayjs } from 'dayjs'
import type { ComponentPublicInstance } from 'vue'
import type { Options } from '@popperjs/core'
import type {
  DateModelType,
  DateOrDates,
  DayOrDays,
  PickerOptions,
  SingleOrRange,
  TimePickerDefaultProps,
  UserInput,
} from './props'
import type { TooltipInstance } from '@jdesign-vue/components/tooltip'

// Date object and string

defineOptions({
  name: 'Picker',
})

const props = defineProps(timePickerDefaultProps)
const emit = defineEmits([
  'update:modelValue',
  'change',
  'focus',
  'blur',
  'calendar-change',
  'panel-change',
  'visible-change',
  'keydown',
])
const attrs = useAttrs()

const { lang } = useLocale()

const nsDate = useNamespace('date')
const nsInput = useNamespace('input')
const nsRange = useNamespace('range')

const { form, formItem } = useFormItem()
const elPopperOptions = inject('RcPopperOptions', {} as Options)

const refPopper = ref<TooltipInstance>()
const inputRef = ref<HTMLElement | ComponentPublicInstance>()
const pickerVisible = ref(false)
const pickerActualVisible = ref(false)

const trackComponentName = computed(() => {
  if (props.type === 'time') return 'TimePicker'
  if (props.type === 'timerange') return 'TimeRangePicker'
  return 'DatePicker'
})

const trackEventName = computed(() => {
  if (trackComponentName.value === 'TimePicker') return 'click'
  if (trackComponentName.value === 'TimeRangePicker') return 'click'
  if (trackComponentName.value === 'DatePicker') return 'click'
  return undefined
})

const trackEventContent = computed(() => {
  if (trackComponentName.value === 'TimePicker') {
    const value = displayValue.value
    return value ? `时间:${value}` : '时间选择器'
  }
  if (trackComponentName.value === 'TimeRangePicker') {
    const value = displayValue.value
    if (isArray(value)) {
      const [start, end] = value
      if (start && end) return `时间范围:${start}-${end}`
      if (start) return `开始时间:${start}`
      if (end) return `结束时间:${end}`
    }
    return '时间范围选择器'
  }
  if (trackComponentName.value === 'DatePicker') {
    const value = displayValue.value
    if (isArray(value)) {
      const [start, end] = value
      if (start && end) return `日期范围:${start}-${end}`
    }
    return value ? `日期:${value}` : '日期选择器'
  }
  return undefined
})

const valueOnOpen = ref<TimePickerDefaultProps['modelValue'] | null>(null)

let hasJustTabExitedInput = false
let ignoreFocusEvent = false

const rangeInputKls = computed(() => [
  nsDate.b('editor'),
  nsDate.bm('editor', props.type),
  nsInput.e('wrapper'),
  nsDate.is('disabled', pickerDisabled.value),
  nsDate.is('active', pickerVisible.value),
  nsRange.b('editor'),
  pickerSize ? nsRange.bm('editor', pickerSize.value) : '',
  attrs.class,
])

const clearIconKls = computed(() => [
  nsInput.e('icon'),
  nsRange.e('close-icon'),
  !showClose.value ? nsRange.e('close-icon--hidden') : '',
])

watch(pickerVisible, (val) => {
  if (!val) {
    userInput.value = null
    nextTick(() => {
      emitChange(props.modelValue)
    })
  } else {
    nextTick(() => {
      if (val) {
        valueOnOpen.value = props.modelValue
      }
    })
  }
})

watch(
  () => props.modelValue,
  (val) => {
    if (props.clearOnClose) {
      valueOnOpen.value = val
    }
  }
)

const emitChange = (
  val: TimePickerDefaultProps['modelValue'] | null,
  isClear?: boolean
) => {
  // determine user real change only
  if (isClear || !valueEquals(val, valueOnOpen.value)) {
    emit('change', val)
    props.validateEvent &&
      formItem?.validate('change').catch((err) => debugWarn(err))
  }
}
const emitInput = (
  input: SingleOrRange<DateModelType | Dayjs> | null,
  clear?: boolean
) => {
  if (!valueEquals(props.modelValue, input)) {
    let formatted
    if (isArray(input)) {
      formatted = input.map((item) =>
        formatter(item, props.valueFormat, lang.value)
      )
    } else if (input) {
      formatted = formatter(input, props.valueFormat, lang.value)
    }
    emit('update:modelValue', input ? formatted : input, lang.value)
  }
}
const emitKeydown = (e: KeyboardEvent) => {
  emit('keydown', e)
}

const refInput = computed<HTMLInputElement[]>(() => {
  if (inputRef.value) {
    const _r = isRangeInput.value
      ? inputRef.value
      : (inputRef.value as any as ComponentPublicInstance).$el
    return Array.from<HTMLInputElement>(_r.querySelectorAll('input'))
  }
  return []
})

const setSelectionRange = (start: number, end: number, pos?: 'min' | 'max') => {
  const _inputs = refInput.value
  if (!_inputs.length) return
  if (!pos || pos === 'min') {
    _inputs[0].setSelectionRange(start, end)
    _inputs[0].focus()
  } else if (pos === 'max') {
    _inputs[1].setSelectionRange(start, end)
    _inputs[1].focus()
  }
}
const focusOnInputBox = (visible = false) => {
  focus(true, true)
  pickerVisible.value = visible
  nextTick(() => {
    ignoreFocusEvent = false
  })
}

const onPick = (date: any = '', visible = false) => {
  if (!visible) {
    // ignoreFocusEvent = true
    focusOnInputBox(visible)
  }
  let result
  if (isArray(date)) {
    result = date.map((_) => (_ ? _.toDate() : _))
  } else {
    // clear btn emit null
    result = date ? date.toDate() : date
  }
  userInput.value = null
  emitInput(result)
}

const onBeforeShow = () => {
  pickerActualVisible.value = true
}

const onShow = () => {
  emit('visible-change', true)
}

const onKeydownPopperContent = (event: KeyboardEvent) => {
  if ((event as KeyboardEvent)?.key === EVENT_CODE.esc) {
    focus(true, true)
  }
}

const onHide = () => {
  pickerActualVisible.value = false
  pickerVisible.value = false
  ignoreFocusEvent = false
  emit('visible-change', false)
}

const handleOpen = () => {
  pickerVisible.value = true
}

const handleClose = () => {
  pickerVisible.value = false
}

const focus = (focusStartInput = true, isIgnoreFocusEvent = false) => {
  ignoreFocusEvent = isIgnoreFocusEvent
  const [leftInput, rightInput] = unref(refInput)
  let input = leftInput
  if (!focusStartInput && isRangeInput.value) {
    input = rightInput
  }
  if (input) {
    input.focus()
  }
}

const handleFocusInput = (e?: FocusEvent) => {
  if (
    props.readonly ||
    pickerDisabled.value ||
    pickerVisible.value ||
    ignoreFocusEvent
  ) {
    return
  }
  pickerVisible.value = true
  emit('focus', e)
}

let currentHandleBlurDeferCallback:
  | (() => Promise<void> | undefined)
  | undefined = undefined

// Check if document.activeElement is inside popper or any input before popper close
const handleBlurInput = (e?: FocusEvent) => {
  const handleBlurDefer = async () => {
    setTimeout(() => {
      if (currentHandleBlurDeferCallback === handleBlurDefer) {
        if (
          !(
            refPopper.value?.isFocusInsideContent() && !hasJustTabExitedInput
          ) &&
          refInput.value.filter((input) => {
            return input.contains(document.activeElement)
          }).length === 0
        ) {
          handleChange()
          pickerVisible.value = false
          emit('blur', e)
          props.validateEvent &&
            formItem?.validate('blur').catch((err) => debugWarn(err))
        }
        hasJustTabExitedInput = false
      }
    }, 0)
  }
  currentHandleBlurDeferCallback = handleBlurDefer
  handleBlurDefer()
}

const pickerDisabled = computed(() => {
  return props.disabled || form?.disabled
})

const parsedValue = computed(() => {
  let dayOrDays: DayOrDays
  if (valueIsEmpty.value) {
    if (pickerOptions.value.getDefaultValue) {
      dayOrDays = pickerOptions.value.getDefaultValue()
    }
  } else {
    if (isArray(props.modelValue)) {
      dayOrDays = props.modelValue.map((d) =>
        parseDate(d, props.valueFormat, lang.value)
      ) as [Dayjs, Dayjs]
    } else {
      dayOrDays = parseDate(props.modelValue, props.valueFormat, lang.value)!
    }
  }

  if (pickerOptions.value.getRangeAvailableTime) {
    const availableResult = pickerOptions.value.getRangeAvailableTime(
      dayOrDays!
    )
    if (!isEqual(availableResult, dayOrDays!)) {
      dayOrDays = availableResult
      emitInput(
        (isArray(dayOrDays)
          ? dayOrDays.map((_) => _.toDate())
          : dayOrDays.toDate()) as SingleOrRange<Date>
      )
    }
  }
  if (isArray(dayOrDays!) && dayOrDays.every((day) => !day)) {
    dayOrDays = [] as unknown as DayOrDays
  }
  return dayOrDays!
})

const displayValue = computed<UserInput>(() => {
  if (!pickerOptions.value.panelReady) return ''
  const formattedValue = formatDayjsToString(parsedValue.value)
  if (isArray(userInput.value)) {
    if (isQuarterPicker.value) {
      return [
        userInput.value[0]
          ? `${dayjs(userInput.value[0]).year()}-Q${dayjs(
              userInput.value[0]
            ).quarter()}`
          : formattedValue && formattedValue[0]
          ? `${dayjs(formattedValue[0]).year()}-Q${dayjs(
              formattedValue[0]
            ).quarter()}`
          : '',
        userInput.value[1]
          ? `${dayjs(userInput.value[1]).year()}-Q${dayjs(
              userInput.value[1]
            ).quarter()}`
          : formattedValue && formattedValue[1]
          ? `${dayjs(formattedValue[1]).year()}-Q${dayjs(
              formattedValue[1]
            ).quarter()}`
          : '',
      ]
    }
    return [
      userInput.value[0] || (formattedValue && formattedValue[0]) || '',
      userInput.value[1] || (formattedValue && formattedValue[1]) || '',
    ]
  } else if (userInput.value !== null) {
    if (isQuarterPicker.value) {
      return `${dayjs(userInput.value).year()}-Q${dayjs(
        userInput.value
      ).quarter()}`
    }
    return userInput.value
  }
  if (!isTimePicker.value && valueIsEmpty.value) return ''
  if (!pickerVisible.value && valueIsEmpty.value) return ''
  if (formattedValue) {
    if (isQuarterPicker.value) {
      if (isArray(formattedValue)) {
        return [
          `${dayjs(formattedValue[0]).year()}-Q${dayjs(
            formattedValue[0]
          ).quarter()}`,
          `${dayjs(formattedValue[1]).year()}-Q${dayjs(
            formattedValue[1]
          ).quarter()}`,
        ]
      }
      return `${dayjs(formattedValue as any).year()}-Q${dayjs(
        formattedValue as any
      ).quarter()}`
    }
    return isDatesPicker.value
      ? (formattedValue as Array<string>).join(', ')
      : formattedValue
  }
  return ''
})

// const isTimeLikePicker = computed(() => props.type.includes('time'))

const isTimePicker = computed(() => props.type.startsWith('time'))

const isDatesPicker = computed(() => props.type === 'dates')

const isQuarterPicker = computed(
  () => props.type === 'quarter' || props.type === 'quarterrange'
)

const isWeekRangePicker = computed(() => props.type === 'weekrange')

const triggerIcon = computed(
  () => props.prefixIcon || (isTimePicker.value ? Clock : Calendar)
)

const showClose = ref(false)

const onClearIconClick = (event: MouseEvent) => {
  if (props.readonly || pickerDisabled.value) return
  if (showClose.value) {
    event.stopPropagation()
    focusOnInputBox(false)
    emitInput(null, true)
    if (props.changeOnClear) {
      emitChange(null, true)
      valueOnOpen.value = null
    }
    showClose.value = false
    pickerVisible.value = false
    pickerOptions.value.handleClear && pickerOptions.value.handleClear()
  }
}

const valueIsEmpty = computed(() => {
  const { modelValue } = props
  return (
    !modelValue || (isArray(modelValue) && !modelValue.filter(Boolean).length)
  )
})

const onMouseDownInput = async (event?: MouseEvent) => {
  if (props.readonly || pickerDisabled.value) return
  // 如果是自定义触发器（如radio-button）调用，或者点击的是非INPUT元素，或者当前已聚焦在输入框上，则显示浮窗
  if (
    props?.customTrigger || // 自定义触发器通过方法调用时可能没有事件对象
    (event && (event.target as HTMLElement)?.tagName !== 'INPUT') ||
    refInput.value.includes(document.activeElement as HTMLInputElement)
  ) {
    pickerVisible.value = true
  }
}
const onMouseEnter = () => {
  if (props.readonly || pickerDisabled.value) return
  if (!valueIsEmpty.value && props.clearable) {
    showClose.value = true
  }
}
const onMouseLeave = () => {
  showClose.value = false
}
const onTouchStartInput = (event: TouchEvent) => {
  if (props.readonly || pickerDisabled.value) return
  if (
    (event.touches[0].target as HTMLElement)?.tagName !== 'INPUT' ||
    refInput.value.includes(document.activeElement as HTMLInputElement)
  ) {
    pickerVisible.value = true
  }
}
const isRangeInput = computed(() => {
  return props.type.includes('range')
})

const pickerSize = useFormSize()

const popperEl = computed(() => unref(refPopper)?.popperRef?.contentRef)
const actualInputRef = computed(() => {
  if (unref(isRangeInput)) {
    return unref(inputRef)
  }

  return (unref(inputRef) as ComponentPublicInstance)?.$el
})

const stopHandle = onClickOutside(actualInputRef, (e: PointerEvent) => {
  const unrefedPopperEl = unref(popperEl)
  const inputEl = unref(actualInputRef)
  if (
    (unrefedPopperEl &&
      (e.target === unrefedPopperEl ||
        e.composedPath().includes(unrefedPopperEl))) ||
    e.target === inputEl ||
    e.composedPath().includes(inputEl)
  )
    return
  pickerVisible.value = false
})

onBeforeUnmount(() => {
  stopHandle?.()
})

const userInput = ref<UserInput>(null)

const handleChange = () => {
  if (userInput.value) {
    const value = parseUserInputToDayjs(displayValue.value)
    if (value) {
      if (isValidValue(value)) {
        emitInput(
          (isArray(value)
            ? value.map((_) => _.toDate())
            : value.toDate()) as DateOrDates
        )
        userInput.value = null
      }
    }
  }
  if (userInput.value === '') {
    emitInput(null)
    emitChange(null)
    userInput.value = null
  }
}

const parseUserInputToDayjs = (value: UserInput) => {
  if (!value) return null
  return pickerOptions.value.parseUserInput!(value)
}

const formatDayjsToString = (value: DayOrDays) => {
  if (!value) return null
  return pickerOptions.value.formatToString!(value)
}

const isValidValue = (value: DayOrDays) => {
  return pickerOptions.value.isValidValue!(value)
}

const handleKeydownInput = async (event: KeyboardEvent) => {
  if (props.readonly || pickerDisabled.value) return

  const { code } = event
  emitKeydown(event)
  if (code === EVENT_CODE.esc) {
    if (pickerVisible.value === true) {
      pickerVisible.value = false
      event.preventDefault()
      event.stopPropagation()
    }
    return
  }

  if (code === EVENT_CODE.down) {
    if (pickerOptions.value.handleFocusPicker) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (pickerVisible.value === false) {
      pickerVisible.value = true
      await nextTick()
    }
    if (pickerOptions.value.handleFocusPicker) {
      pickerOptions.value.handleFocusPicker()
      return
    }
  }

  if (code === EVENT_CODE.tab) {
    hasJustTabExitedInput = true
    return
  }

  if (code === EVENT_CODE.enter || code === EVENT_CODE.numpadEnter) {
    if (
      userInput.value === null ||
      userInput.value === '' ||
      isValidValue(parseUserInputToDayjs(displayValue.value) as DayOrDays)
    ) {
      handleChange()
      pickerVisible.value = false
    }
    event.stopPropagation()
    return
  }

  // if user is typing, do not let picker handle key input
  if (userInput.value) {
    event.stopPropagation()
    return
  }
  if (pickerOptions.value.handleKeydownInput) {
    pickerOptions.value.handleKeydownInput(event)
  }
}
const onUserInput = (e: string) => {
  userInput.value = e
  // Temporary fix when the picker is dismissed and the input box
  // is focused, just mimic the behavior of antdesign.
  if (!pickerVisible.value) {
    pickerVisible.value = true
  }
}

const handleStartInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (userInput.value) {
    userInput.value = [target.value, userInput.value[1]]
  } else {
    userInput.value = [target.value, null]
  }
}

const handleEndInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (userInput.value) {
    userInput.value = [userInput.value[0], target.value]
  } else {
    userInput.value = [null, target.value]
  }
}

const handleStartChange = () => {
  const values = userInput.value as string[]
  const value = parseUserInputToDayjs(values && values[0]) as Dayjs
  const parsedVal = unref(parsedValue) as [Dayjs, Dayjs]
  if (value && value.isValid()) {
    userInput.value = [
      formatDayjsToString(value) as string,
      displayValue.value?.[1] || null,
    ]
    const newValue = [value, parsedVal && (parsedVal[1] || null)] as DayOrDays
    if (isValidValue(newValue)) {
      emitInput(newValue)
      userInput.value = null
    }
  }
}

const handleEndChange = () => {
  const values = unref(userInput) as string[]
  const value = parseUserInputToDayjs(values && values[1]) as Dayjs
  const parsedVal = unref(parsedValue) as [Dayjs, Dayjs]
  if (value && value.isValid()) {
    userInput.value = [
      unref(displayValue)?.[0] || null,
      formatDayjsToString(value) as string,
    ]
    const newValue = [parsedVal && parsedVal[0], value] as DayOrDays
    if (isValidValue(newValue)) {
      emitInput(newValue)
      userInput.value = null
    }
  }
}

const pickerOptions = ref<Partial<PickerOptions>>({})
const onSetPickerOption = <T extends keyof PickerOptions>(
  e: [T, PickerOptions[T]]
) => {
  pickerOptions.value[e[0]] = e[1]
  pickerOptions.value.panelReady = true
}

const onCalendarChange = (e: [Date, false | Date]) => {
  emit('calendar-change', e)
}

const onPanelChange = (
  value: [Dayjs, Dayjs] | Dayjs,
  mode: 'month' | 'year' | 'quarter',
  view: unknown
) => {
  emit('panel-change', value, mode, view)
}

provide('EP_PICKER_BASE', {
  props,
})

defineExpose({
  /**
   * @description focus input box.
   */
  focus,
  /**
   * @description emit focus event
   */
  handleFocusInput,
  /**
   * @description emit blur event
   */
  handleBlurInput,
  /**
   * @description opens picker
   */
  handleOpen,
  /**
   * @description closes picker
   */
  handleClose,
  /**
   * @description pick item manually
   */
  onPick,
})
</script>
